import { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard as Edit3, Trash2, Send, Clock, CheckCircle2, AlertCircle, ExternalLink, Image, ChevronRight, RefreshCw, AtSign, Upload, RotateCcw, Layers } from 'lucide-react';
import { chPostService, chCommentService } from '../services/postService';
import { chProfileService, chUserProjectService } from '../services';
import { chNotificationService } from '../services/notificationService';
import { MentionTextarea, MentionText } from './MentionTextarea';
import { extractMentionIds, splitMentions, type MentionCandidate } from '../lib/mentions';
import type { ChApproval, ChPostVersion } from '../services/postService';
import { useChAuth } from '../contexts/ChAuthContext';
import { useModal } from '../../contexts/ModalContext';
import { AiAdaptPlatformsPanel } from './AiAdaptPlatformsPanel';
import { AiFeedbackRevision } from './AiFeedbackRevision';
import { MediaPreview, isVideoUrl } from './MediaPreview';
import { MediaCarousel } from './MediaCarousel';
import { DeadlineBadge } from './DeadlineBadge';
import { getAssigneeIds, getMediaUrls, getPlatforms, MAX_MEDIA, VIDEO_CONTENT_TYPES } from '../lib/posts';
import { PostStatsPanel } from './PostStatsPanel';
import type { ChPost, ChComment, ChPostStatus, ChPostPlatform, ChProfile } from '../types';
import { POST_STATUS_LABELS, PLATFORM_LABELS, CONTENT_TYPE_LABELS, ROLE_LABELS, isChStaffRole } from '../types';

interface Props {
  post: ChPost;
  onBack: () => void;
  onEdit: (post: ChPost) => void;
  onDelete: (postId: string) => void;
  onStatusChange: (postId: string, status: ChPostStatus) => void;
  onNewVersion?: (post: ChPost, changeNote: string) => void;
  onCreateAdaptedPost?: (platform: ChPostPlatform, caption: string, sourcePost: ChPost) => void;
  onPostUpdated?: (post: ChPost) => void;
}

const APPROVAL_ACTION_LABELS: Record<string, string> = {
  internal_approved: 'Aprovação interna',
  client_approved: 'Aprovação do cliente',
  changes_requested: 'Alterações pedidas',
  published: 'Publicado',
  unapproved: 'Desaprovado',
};

const NEXT_STATUS: Partial<Record<ChPostStatus, ChPostStatus>> = {
  ideia: 'em_producao',
  em_producao: 'pronto',
  pronto: 'em_revisao',
  em_revisao: 'aprovado',
  aprovado: 'publicado',
};

export function PostDetail({ post, onBack, onEdit, onDelete, onStatusChange, onNewVersion, onCreateAdaptedPost, onPostUpdated }: Props) {
  const { user } = useChAuth();
  const { showConfirm, toast } = useModal();
  const [comments, setComments] = useState<ChComment[]>([]);
  const [approvals, setApprovals] = useState<ChApproval[]>([]);
  const [versions, setVersions] = useState<ChPostVersion[]>([]);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const [people, setPeople] = useState<ChProfile[]>([]);
  const [clientMemberIds, setClientMemberIds] = useState<Set<string>>(new Set());
  const [showVersions, setShowVersions] = useState(false);
  const [versionFiles, setVersionFiles] = useState<string[] | null>(null);
  const [versionNote, setVersionNote] = useState('');
  const [versionBusy, setVersionBusy] = useState(false);
  const [mentionKey, setMentionKey] = useState(0);

  useEffect(() => {
    loadData();
  }, [post.id]);

  // Who can be tagged: the team always; the project's clients only in
  // comments they can see
  useEffect(() => {
    // The team can always be tagged even if the project's members don't load
    chProfileService.getAll()
      .then(setPeople)
      .catch(() => { /* tagging just won't suggest anyone */ });
    chUserProjectService.getProjectMembers(post.project_id)
      .then(members => setClientMemberIds(new Set(
        members.filter(m => !isChStaffRole(m.profile.role)).map(m => m.user_id)
      )))
      .catch(() => setClientMemberIds(new Set()));
  }, [post.project_id]);

  const mentionCandidates: MentionCandidate[] = people
    .filter(p => p.id !== user?.id)
    .filter(p => isChStaffRole(p.role) || (visibility === 'client' && clientMemberIds.has(p.id)))
    .map(p => ({ id: p.id, name: p.name, hint: ROLE_LABELS[p.role] }));
  const nameById = new Map(people.map(p => [p.id, p.name]));

  async function loadData() {
    const [commentsData, approvalsData, versionsData] = await Promise.all([
      chCommentService.getByPost(post.id),
      chPostService.getApprovals(post.id),
      chPostService.getVersions(post.id),
    ]);
    setComments(commentsData);
    setApprovals(approvalsData);
    setVersions(versionsData);
  }

  async function handleSendComment(e?: React.FormEvent) {
    e?.preventDefault();
    const text = newComment.trim();
    if (!text || !user || sendingComment) return;

    setSendingComment(true);
    try {
      const mentions = extractMentionIds(text, mentionCandidates);
      const comment = await chCommentService.create({
        post_id: post.id,
        author_id: user.id,
        author_name: user.name,
        type: visibility,
        text,
        mentions,
      });
      setComments(prev => [...prev, { ...comment, mentions: comment.mentions ?? mentions }]);
      setNewComment('');

      // Notifications are best-effort: the comment is already saved
      const snippet = text.length > 140 ? `${text.slice(0, 140)}…` : text;
      await Promise.all(mentions.map(id => chNotificationService.create({
        user_id: id,
        post_id: post.id,
        project_id: post.project_id,
        type: 'mentioned',
        message: `${user.name} mencionou-te em "${post.title}": ${snippet}`,
      }))).then(() => {
        if (mentions.length > 0) {
          const names = mentions.map(id => nameById.get(id)?.split(' ')[0]).filter(Boolean).join(', ');
          toast.success(`Notificado: ${names}`);
        }
      }).catch(() => toast.warning('Comentário enviado, mas não foi possível notificar as pessoas mencionadas'));

      if (visibility === 'client') {
        await chNotificationService.notifyProjectClients(
          post.project_id,
          post.id,
          'commented',
          `${user.name} respondeu em "${post.title}"`
        ).catch(() => { /* clients still see it on their page */ });
      }
    } catch {
      toast.error('Erro ao enviar o comentário');
    } finally {
      setSendingComment(false);
    }
  }

  async function handleVersionFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []).slice(0, MAX_MEDIA);
    e.target.value = '';
    if (files.length === 0) return;
    setVersionBusy(true);
    try {
      const urls: string[] = [];
      for (const file of files) urls.push(await chPostService.uploadMedia(file, post.project_id));
      setVersionFiles(urls);
    } catch {
      toast.error('Não foi possível carregar os ficheiros');
    } finally {
      setVersionBusy(false);
    }
  }

  /** New files replace the current ones; the old ones stay in the history. */
  async function saveNewVersion() {
    if (!versionFiles || !user) return;
    setVersionBusy(true);
    try {
      const updated = await chPostService.update(
        post.id,
        { media_urls: versionFiles, media_url: versionFiles[0] || null },
        { note: versionNote, authorId: user.id, authorName: user.name }
      );
      onPostUpdated?.(updated);
      setVersionFiles(null);
      setVersionNote('');
      setVersions(await chPostService.getVersions(post.id));
      setShowVersions(true);
      toast.success(`Guardado como v${updated.current_version}`);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível guardar a versão', 8000);
    } finally {
      setVersionBusy(false);
    }
  }

  async function restoreVersion(v: ChPostVersion) {
    if (!user) return;
    const confirmed = await showConfirm({
      title: `Repor a v${v.version_number}?`,
      message: 'O texto e os ficheiros dessa versão voltam a ser os atuais. Fica guardada como uma versão nova; nada se perde.',
      confirmText: 'Repor',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    const media = v.media_urls && v.media_urls.length > 0 ? v.media_urls : v.media_url ? [v.media_url] : [];
    try {
      const updated = await chPostService.update(
        post.id,
        {
          title: v.title,
          caption: v.caption,
          media_urls: media,
          media_url: media[0] || null,
          ...(v.thumbnail_url !== undefined ? { thumbnail_url: v.thumbnail_url } : {}),
        },
        { note: `Reposta a v${v.version_number}`, authorId: user.id, authorName: user.name }
      );
      onPostUpdated?.(updated);
      setVersions(await chPostService.getVersions(post.id));
      toast.success(updated.current_version === post.current_version ? 'Já era igual à versão atual' : `v${v.version_number} reposta como v${updated.current_version}`);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível repor a versão', 8000);
    }
  }

  async function handleDelete() {
    const confirmed = await showConfirm({
      title: 'Eliminar post',
      message: 'Eliminar este post? Esta ação é irreversível.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (confirmed) onDelete(post.id);
  }

  const mediaUrls = getMediaUrls(post);
  const isVideo = VIDEO_CONTENT_TYPES.includes(post.content_type) || mediaUrls.some(isVideoUrl);
  const assigneeNames = getAssigneeIds(post).map(id => nameById.get(id)).filter(Boolean) as string[];
  const nextStatus = NEXT_STATUS[post.status];
  const canAdvance = user?.role === 'admin' || user?.role === 'project_manager';

  // What the client wrote (staff replies are client-visible too, but they
  // aren't the change request)
  const staffIds = new Set(people.filter(p => isChStaffRole(p.role)).map(p => p.id));
  const clientComments = comments.filter(c => c.type === 'client' && !(c.author_id && staffIds.has(c.author_id)));
  const clientChangeComment = clientComments.length > 0
    ? clientComments[clientComments.length - 1].text
    : null;

  return (
    <div className="ch-animate-in space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="ch-btn ch-btn-ghost ch-btn-sm">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => onEdit(post)} className="ch-btn ch-btn-ghost ch-btn-sm">
            <Edit3 className="w-3.5 h-3.5" />
            Editar
          </button>
          <button
            onClick={handleDelete}
            className="ch-btn ch-btn-ghost ch-btn-sm text-red-500"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Media preview */}
          {mediaUrls.length > 0 ? (
            <div className="ch-card p-0 overflow-hidden">
              <MediaCarousel
                urls={mediaUrls}
                alt={post.title}
                poster={post.thumbnail_url}
                mediaClassName="w-full h-[400px] object-contain bg-gray-900"
                accent="#ffffff"
              />
              {post.thumbnail_url && isVideo && (
                <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
                  <a href={post.thumbnail_url} target="_blank" rel="noopener noreferrer" className="w-10 aspect-[9/16] rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={post.thumbnail_url} alt="Capa" className="w-full h-full object-cover" />
                  </a>
                  <p className="text-xs text-gray-500">Capa / miniatura</p>
                </div>
              )}
            </div>
          ) : (
            <div className="ch-card flex items-center justify-center h-48 bg-gray-50">
              <Image className="w-12 h-12 text-gray-200" />
            </div>
          )}

          {/* Client feedback banner */}
          {post.client_decision === 'changes_requested' && (
            <div className="p-4 rounded-2xl bg-[var(--ch-magenta)]/10 border border-[var(--ch-magenta)]/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[var(--ch-magenta)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[var(--ch-magenta)]">Alterações pedidas pelo cliente</p>
                  {clientChangeComment && (
                    <p className="text-sm text-gray-700 mt-1 italic">"{clientChangeComment}"</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {onNewVersion && (
                      <button
                        onClick={() => onNewVersion(post, clientChangeComment || '')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-[var(--ch-magenta)] text-white hover:opacity-90 transition-opacity"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Criar nova versão
                      </button>
                    )}
                    {post.caption && onNewVersion && (
                      <AiFeedbackRevision
                        projectId={post.project_id}
                        postId={post.id}
                        title={post.title}
                        currentCaption={post.caption}
                        clientComments={clientComments.map(c => c.text)}
                        onUseVersion={(caption, changeNote) => onNewVersion({ ...post, caption }, changeNote)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Post info */}
          <div className="ch-card space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="ch-heading ch-heading-md">{post.title}</h2>
              <span className={`ch-badge ch-badge-${post.status} flex-shrink-0`}>
                {POST_STATUS_LABELS[post.status]}
              </span>
            </div>

            {post.caption && (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{post.caption}</p>
            )}

            <div className="flex flex-wrap gap-2">
              {getPlatforms(post).map(p => (
                <span key={p} className="ch-chip">{PLATFORM_LABELS[p]}</span>
              ))}
              <span className="ch-chip">{CONTENT_TYPE_LABELS[post.content_type]}</span>
              {mediaUrls.length > 1 && (
                <span className="ch-chip"><Layers className="w-3 h-3" />{mediaUrls.length} slides</span>
              )}
              {post.published_at ? (
                <span className="ch-chip">
                  <Clock className="w-3 h-3" />
                  Publicado a {new Date(post.published_at).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              ) : post.scheduled_date && (
                <span className="ch-chip">
                  <Clock className="w-3 h-3" />
                  {new Date(post.scheduled_date).toLocaleDateString('pt-PT')}
                </span>
              )}
              <DeadlineBadge post={post} />
            </div>

            {post.external_link && (
              <a
                href={post.external_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[var(--ch-blue)] font-medium hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {post.status === 'publicado' ? 'Ver publicação' : 'Link externo'}
              </a>
            )}

            {/* AI Adapt Platforms */}
            {post.caption && (
              <AiAdaptPlatformsPanel
                projectId={post.project_id}
                postId={post.id}
                title={post.title}
                caption={post.caption}
                currentPlatform={getPlatforms(post)[0]}
                onCreatePost={onCreateAdaptedPost ? (platform, caption) => onCreateAdaptedPost(platform, caption, post) : undefined}
              />
            )}

            {/* Status advance */}
            {canAdvance && nextStatus && (
              <button
                onClick={() => onStatusChange(post.id, nextStatus)}
                className="ch-btn ch-btn-primary ch-btn-sm"
              >
                <ChevronRight className="w-3.5 h-3.5" />
                Avançar para {POST_STATUS_LABELS[nextStatus]}
              </button>
            )}
          </div>

          {/* Stats and prints */}
          {canAdvance && onPostUpdated && (
            <PostStatsPanel post={post} onPostUpdated={onPostUpdated} />
          )}

          {/* Comments */}
          <div className="ch-card">
            <h3 className="text-sm font-bold text-[var(--ch-ink)] mb-4">
              Comentários ({comments.length})
            </h3>

            {comments.length === 0 ? (
              <p className="text-sm text-gray-400 mb-4">Ainda sem comentários.</p>
            ) : (
              <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.id} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[var(--ch-ink)] flex items-center justify-center text-[10px] font-bold text-[var(--ch-lime)] flex-shrink-0">
                      {c.author_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--ch-ink)]">{c.author_name}</span>
                        {c.type === 'client' && (
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Cliente vê</span>
                        )}
                        <span className="text-[10px] text-gray-400">
                          {new Date(c.created_at).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">
                        <MentionText parts={splitMentions(c.text, (c.mentions || []).map(id => nameById.get(id) || ''))} />
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSendComment} className="space-y-2">
              <div className="inline-flex items-center bg-gray-100 rounded-full p-0.5" role="radiogroup" aria-label="Quem vê o comentário">
                {([
                  { key: 'internal', label: 'Nota interna' },
                  { key: 'client', label: 'Visível ao cliente' },
                ] as const).map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={visibility === opt.key}
                    onClick={() => setVisibility(opt.key)}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                      visibility === opt.key ? 'bg-[var(--ch-ink)] text-white' : 'text-gray-500 hover:text-[var(--ch-ink)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <MentionTextarea
                    focusSignal={mentionKey}
                    value={newComment}
                    onChange={setNewComment}
                    candidates={mentionCandidates}
                    onSubmit={() => handleSendComment()}
                    rows={2}
                    className="ch-textarea text-sm min-h-0"
                    placeholder={visibility === 'client'
                      ? 'Resposta que o cliente vê… (@ para mencionar)'
                      : 'Nota só para a equipa… (@ para mencionar)'}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Starts a tag: the picker opens on the "@"
                    setNewComment(c => (c && !/\s$/.test(c) ? `${c} @` : `${c}@`));
                    setMentionKey(k => k + 1);
                  }}
                  className="ch-btn ch-btn-ghost ch-btn-sm"
                  title="Mencionar alguém"
                  aria-label="Mencionar alguém"
                >
                  <AtSign className="w-3.5 h-3.5" />
                </button>
                <button
                  type="submit"
                  disabled={sendingComment || !newComment.trim()}
                  className="ch-btn ch-btn-primary ch-btn-sm"
                  title="Enviar (⌘/Ctrl + Enter)"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Versions */}
          <div className="ch-card">
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="w-full flex items-center justify-between"
            >
              <h3 className="text-sm font-bold text-[var(--ch-ink)]">
                Versões ({versions.length})
              </h3>
              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showVersions ? 'rotate-90' : ''}`} />
            </button>

            {canAdvance && (
              <div className="mt-3">
                {versionFiles ? (
                  <div className="p-2.5 rounded-xl bg-gray-50 space-y-2">
                    <div className="flex gap-1.5 overflow-x-auto">
                      {versionFiles.map((url, i) => (
                        <div key={`${url}-${i}`} className="w-12 h-12 rounded-md overflow-hidden bg-gray-200 flex-shrink-0">
                          <MediaPreview url={url} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <input
                      type="text"
                      className="ch-input h-8 text-xs"
                      placeholder="O que mudou? (opcional)"
                      value={versionNote}
                      onChange={e => setVersionNote(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button onClick={saveNewVersion} disabled={versionBusy} className="ch-btn ch-btn-primary ch-btn-sm flex-1">
                        {versionBusy ? 'A guardar...' : `Guardar como v${post.current_version + 1}`}
                      </button>
                      <button onClick={() => { setVersionFiles(null); setVersionNote(''); }} className="ch-btn ch-btn-ghost ch-btn-sm">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className={`ch-btn ch-btn-ghost ch-btn-sm w-full cursor-pointer ${versionBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Upload className="w-3.5 h-3.5" />
                    {versionBusy ? 'A carregar...' : 'Substituir ficheiros (nova versão)'}
                    <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleVersionFiles} />
                  </label>
                )}
              </div>
            )}

            {showVersions && versions.length > 0 && (
              <div className="mt-3 space-y-2 max-h-[360px] overflow-y-auto">
                {versions.map(v => {
                  const files = v.media_urls && v.media_urls.length > 0 ? v.media_urls : v.media_url ? [v.media_url] : [];
                  const isCurrent = v.version_number === post.current_version;
                  return (
                    <div key={v.id} className="p-2.5 rounded-xl bg-gray-50 text-xs space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">
                          v{v.version_number}
                          {isCurrent && <span className="ml-1.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[var(--ch-lime)] text-[var(--ch-ink)]">Atual</span>}
                        </span>
                        <span className="text-gray-400">
                          {new Date(v.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {(v.note || v.author_name) && (
                        <p className="text-gray-500">
                          {v.note}
                          {v.note && v.author_name && ' · '}
                          {v.author_name && <span className="text-gray-400">{v.author_name}</span>}
                        </p>
                      )}
                      {files.length > 0 && (
                        <div className="flex gap-1.5 overflow-x-auto">
                          {files.map((url, i) => (
                            <a key={`${url}-${i}`} href={url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-md overflow-hidden bg-gray-200 flex-shrink-0" title={`Abrir ficheiro ${i + 1}`}>
                              <MediaPreview url={url} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                      {canAdvance && !isCurrent && (
                        <button onClick={() => restoreVersion(v)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--ch-blue)] hover:underline">
                          <RotateCcw className="w-3 h-3" />
                          Repor esta versão
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Approval timeline */}
          <div className="ch-card">
            <h3 className="text-sm font-bold text-[var(--ch-ink)] mb-3">
              Histórico de Aprovação
            </h3>

            {approvals.length === 0 ? (
              <p className="text-xs text-gray-400">Sem atividade registada.</p>
            ) : (
              <div className="space-y-3">
                {approvals.map(a => (
                  <div key={a.id} className="flex gap-2.5">
                    <div className="flex-shrink-0 mt-0.5">
                      {a.action === 'changes_requested' ? (
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-[var(--ch-ink)]">
                        {APPROVAL_ACTION_LABELS[a.action] || a.action}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {a.user_name} &middot;{' '}
                        {new Date(a.created_at).toLocaleDateString('pt-PT', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      {a.note && (
                        <p className="text-xs text-gray-500 mt-0.5 italic">{a.note}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="ch-card text-xs space-y-2">
            <h3 className="font-bold text-[var(--ch-ink)]">Detalhes</h3>
            <div className="flex justify-between">
              <span className="text-gray-400">Versão</span>
              <span className="font-medium">v{post.current_version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Criado</span>
              <span className="font-medium">{new Date(post.created_at).toLocaleDateString('pt-PT')}</span>
            </div>
            {post.scheduled_date && (
              <div className="flex justify-between">
                <span className="text-gray-400">Publicação</span>
                <span className="font-medium">{new Date(post.scheduled_date).toLocaleDateString('pt-PT')}</span>
              </div>
            )}
            {post.due_date && (
              <div className="flex justify-between">
                <span className="text-gray-400">Prazo interno</span>
                <span className="font-medium">{new Date(post.due_date).toLocaleDateString('pt-PT')}</span>
              </div>
            )}
            {assigneeNames.length > 0 && (
              <div className="flex justify-between gap-3">
                <span className="text-gray-400">Responsáveis</span>
                <span className="font-medium text-right">{assigneeNames.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
